import React, { ReactNode, useCallback, useContext, useState } from "react";
import { setZodLocale } from "../modules/shared/validations/zod";

export interface ILocaleProvider {
  currentLocale: string;
  shortLocale: string;
  setLocale: (newLocale: string) => void;
}

export const availableLocales = [
  { id: "header.spanish", label: "Español", code: "es-ES", locale: "es" },
  { id: "header.english", label: "English", code: "en-US", locale: "en" },
  { id: "header.french", label: "Français", code: "fr-FR", locale: "fr" },
] as const;

export type SupportedLocale = typeof availableLocales[number]["code"];

const DEFAULT_LOCALE: SupportedLocale = "es-ES";

export function normalizeLocale(raw: string | null | undefined): SupportedLocale {
  if (!raw) return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();
  const exact = availableLocales.find(l => l.code.toLowerCase() === lower);
  if (exact) return exact.code;
  const prefix = lower.split("-")[0];
  const byPrefix = availableLocales.find(l => l.locale === prefix);
  return byPrefix?.code ?? DEFAULT_LOCALE;
}

const localeKey = "locale";
const savedLanguage = normalizeLocale(window.localStorage.getItem(localeKey) || navigator.language);
const LanguageContext = React.createContext<ILocaleProvider>({
  currentLocale: savedLanguage,
  shortLocale: languageFromLocaleString(savedLanguage),
  setLocale: () => {},
});

export interface LocaleProviderProps {
  children: (lang: string) => ReactNode;
}

export function LocaleProvider(props: LocaleProviderProps) {
  const [lang, setLang] = useState(savedLanguage);

  const setLanguage = useCallback((newLocale: string) => {
    const normalized = normalizeLocale(newLocale);
    window.localStorage.setItem(localeKey, normalized);
    setLang(normalized);
    setZodLocale(normalized);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        setLocale: setLanguage,
        currentLocale: lang,
        shortLocale: languageFromLocaleString(lang),
      }}>
      {props.children(lang)}
    </LanguageContext.Provider>
  );
}

export function useLocaleProvider(): ILocaleProvider {
  return useContext(LanguageContext);
}

export function languageFromLocaleString(localeString: string) {
  const pieces = localeString.split("-");
  return pieces[0];
}
